import { useEffect, useState } from "react";
import Dialog from "components/Overlays/ResponsiveDialog";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { FiUploadCloud, FiInfo } from "react-icons/fi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import useUploadBookFiles from "api/BookResource/useUploadBookFiles";
import {
	getUploadFileSteps,
	getUploadFilesButtonLabel,
	hasSelectedFiles,
	isAcceptedBookFile,
} from "api/BookResource/bookFileUpload";
import { MAX_BOOK_COVER_MB, MAX_BOOK_PDF_MB } from "utils";
import BookFilesFields from "./BookFilesFields";
import BookSubmissionProgress from "./BookSubmissionProgress";

const BookFilesUploadModal = ({
	visible,
	onHide,
	bookId,
	bookTitle,
	hasExistingCover = false,
	hasExistingFile = false,
	activateAfterUpload = false,
	allowSkip = true,
}) => {
	const [uploadStep, setUploadStep] = useState(null);
	const [uploadError, setUploadError] = useState("");

	const { mutateAsync, isLoading } = useUploadBookFiles(bookId, {
		onStepChange: setUploadStep,
		activateAfterUpload,
	});

	useEffect(() => {
		if (visible) {
			setUploadStep(null);
			setUploadError("");
		}
	}, [visible, hasExistingCover, hasExistingFile]);

	const initialValues = {
		book_cover: null,
		book_file: null,
	};

	const validationSchema = Yup.object().shape({
		book_cover: Yup.mixed()
			.nullable()
			.test(
				"fileSize",
				`Cover image size should not exceed ${MAX_BOOK_COVER_MB}MB`,
				(value) => !value || value.size / 1024 / 1024 <= MAX_BOOK_COVER_MB,
			),
		book_file: Yup.mixed()
			.nullable()
			.test(
				"fileSize",
				`PDF size should not exceed ${MAX_BOOK_PDF_MB}MB`,
				(value) => !value || value.size / 1024 / 1024 <= MAX_BOOK_PDF_MB,
			)
			.test("mimeType", "Only PDF documents are allowed", isAcceptedBookFile),
	});

	const handleSubmit = async (values) => {
		if (!hasSelectedFiles(values)) {
			return;
		}

		setUploadError("");
		try {
			await mutateAsync(values);
			onHide?.();
		} catch (error) {
			setUploadError(error?.response?.data?.message || "The upload could not be completed. Your files are still selected. Please try again.");
		}
	};

	const uploadSteps = getUploadFileSteps({ activateAfterUpload });

	return (
		<Dialog
			visible={visible}
			onHide={onHide}
			header="Upload book files"
			description={bookTitle || "Add a cover and PDF to bring your book to the library."}
			icon={<FiUploadCloud />}
			closable={!isLoading}
			closeOnEscape={!isLoading}
			style={{ width: "min(680px, 95vw)" }}
			position="center"
			draggable={false}
			modal
			className="akello-book-files-dialog"
		>
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
				enableReinitialize
			>
				{({ errors, isValid, setFieldTouched, setFieldValue, touched, values }) => (
					<Form className="space-y-5" aria-busy={isLoading}>
						<BookFilesFields
							disabled={isLoading}
							values={values}
							errors={errors}
							touched={touched}
							setFieldValue={setFieldValue}
							setFieldTouched={setFieldTouched}
							hasExistingCover={hasExistingCover}
							hasExistingFile={hasExistingFile}
						/>

						{activateAfterUpload && (
							<p className="akello-form-notice">
								<FiInfo aria-hidden="true" /><span>This book will be activated automatically after both the cover and book file are
								uploaded.</span>
							</p>
						)}

						{uploadError && <div role="alert" className="akello-form-error"><strong>Upload interrupted</strong><p>{uploadError}</p></div>}
						{isLoading && <BookSubmissionProgress steps={uploadSteps} currentStep={uploadStep} />}

						<div className="akello-form-actions is-sticky">
							{allowSkip && (
								<button
									type="button"
									onClick={onHide}
									disabled={isLoading}
									className="akello-action"
								>
									{allowSkip === "create" ? "Skip for now" : "Cancel"}
								</button>
							)}
							<button
								type="submit"
								disabled={isLoading || !hasSelectedFiles(values) || !isValid}
								className="akello-action akello-action-primary"
							>
								{uploadError && !isLoading ? "Retry upload" : getUploadFilesButtonLabel({
									isLoading,
									uploadStep,
									activateAfterUpload,
								})}
								{isLoading && <AiOutlineLoading3Quarters size={16} className="animate-spin" />}
							</button>
						</div>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default BookFilesUploadModal;
